/**
 * Adjustment Preview Component
 *
 * 顯示調整變更預覽
 * - 變更清單
 * - 衝突警告
 * - 說明文字
 */

import React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Clock, FileText } from '../common/icons';
import { AdjustmentResultData } from '../../types/morningKickoff';

interface AdjustmentPreviewProps {
  data: AdjustmentResultData;
  onApply: () => void;
  onCancel: () => void;
  isApplying?: boolean;
}

export const AdjustmentPreview: React.FC<AdjustmentPreviewProps> = ({
  data,
  onApply,
  onCancel,
  isApplying = false
}) => {
  const { changes, warnings, explanation } = data;

  // 檢查是否有失敗的變更
  const hasFailures = changes.some(c => !c.success);
  const hasConflicts = changes.some(c => c.conflicts && c.conflicts.length > 0);

  return (
    <div className="space-y-4">
      {/* 標題 */}
      <div className="flex items-start gap-2">
        <CheckCircle2 size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900">
            Got it! Here's what will change:
          </h3>
          {explanation && (
            <p className="text-sm text-gray-600 mt-1">
              {explanation}
            </p>
          )}
        </div>
      </div>

      {/* 變更清單 */}
      <div className="space-y-3">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <FileText size={16} className="text-orange-600" />
          Changes
        </h4>

        {changes.map((change, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border ${
              change.success
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-start gap-2">
              {change.success ? (
                <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              )}

              <div className="flex-1 min-w-0">
                {/* 項目標題 */}
                <div className="font-medium text-gray-900 text-sm">
                  {change.itemTitle}
                </div>

                {/* 時間變更 */}
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                  <Clock size={14} />
                  <span className="line-through">{change.originalTime}</span>
                  <span>→</span>
                  <span className="font-semibold text-green-700">{change.newTime}</span>
                </div>

                {/* 錯誤訊息 */}
                {!change.success && change.error && (
                  <div className="mt-2 text-sm text-red-700">
                    {change.error}
                  </div>
                )}

                {/* 衝突訊息 */}
                {change.conflicts && change.conflicts.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-1 text-xs font-semibold text-orange-700">
                      <AlertTriangle size={14} />
                      Conflicts:
                    </div>
                    {change.conflicts.map((conflict, cIndex) => (
                      <div key={cIndex} className="text-xs text-orange-700 ml-4">
                        • {conflict.title} ({conflict.startTime} - {conflict.endTime})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 警告訊息 */}
      {warnings.length > 0 && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-orange-900">
                Heads up
              </h4>
              <ul className="mt-2 space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index} className="text-sm text-orange-800">
                    • {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 操作按鈕 */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onCancel}
          disabled={isApplying}
          className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>

        <button
          onClick={onApply}
          disabled={isApplying || hasFailures || hasConflicts}
          className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {isApplying ? 'Applying...' : 'Apply Changes'}
        </button>
      </div>

      {/* 提示文字 */}
      {(hasFailures || hasConflicts) && (
        <p className="text-xs text-center text-gray-500">
          Please resolve conflicts or errors before applying changes
        </p>
      )}
    </div>
  );
};
